import { createSlice, current } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import { PLATFORMS } from '@sonifi/wb-screenbuilder/constants/wbScreenBuilderConstants';

const initialState = {
  emailOrdering: false,
  irdSettings: {
    additional_fees_message: '',
    adminChanges: {
      hasChanges: false,
      from: null,
      to: null,
    },
    created: '',
    default_category_image: [{}],
    default_item_image: [{}],
    delivery_fee_label: '',
    delivery_fee: null,
    delivery_timeframe_max: null,
    delivery_timeframe: null,
    disclaimer: '',
    display_fees_with_subtotal: true,
    display_item_images: true,
    display_item_prices: true,
    display_name: '',
    email_addresses: [],
    external_contact_message: null,
    group: '',
    id: null,
    include_guest_name_on_order: true,
    internal_contact_message: '',
    intro_message: '',
    last_updated: '',
    ordering_mode: '',
    ordering_unavailable_message: '',
    orderingModes: [],
    printer_ip: null,
    questions: [],
    revenue_center: '',
    secondary_message: '',
    service_charge_label: '',
    service_charge: null,
    tax: null,
    thank_you_message: '',
    uri: '',
    version: null,
    ipOne: null,
    ipTwo: null,
    ipThree: null,
    ipFour: null,
  },
  isOnSite: false,
  isReadOnly: false,
  loadingIrdSettings: LOADING_STATUS.notLoading,
  loadingIrdSettingsSubmit: LOADING_STATUS.notLoading,
  productSettings: null,
  revenueCenters: [],
  revenueCenterSelected: null,
};

export const irdSettingsSlice = createSlice({
  name: 'irdSettings',
  initialState,
  reducers: {
    setRevenueCenters(state, action) {
      const { revenueCenters } = action.payload;
      state.revenueCenters = revenueCenters;
    },
    setRevenueCenter(state, action) {
      const { revenueCenter } = action.payload;
      state.revenueCenterSelected = revenueCenter;
    },
    updateRevenueCenter(state, action) {
      const { revenueCenter } = action.payload;
      const { revenueCenters, revenueCenterSelected } = current(state);
      state.revenueCenters = revenueCenters.map((rc) =>
        rc.id === revenueCenter.id ? revenueCenter : rc,
      );
      if (revenueCenterSelected.id === revenueCenter.id) {
        state.revenueCenterSelected = revenueCenter;
      }
    },
    setIrdSettings(state, action) {
      const { irdSettings, productSettings, imagesDependencies, platform } = action.payload;
      irdSettings.default_item_image = imagesDependencies.defaultItemImage;

      if (platform !== PLATFORMS.hctv) {
        irdSettings.default_category_image = imagesDependencies.defaultCategoryImage;
      }

      const isOnSite =
        productSettings.available_ordering_modes.on_site &&
        irdSettings.ordering_mode === 'on_site';
      state.isReadOnly =
        productSettings.available_ordering_modes.read_only &&
        irdSettings.ordering_mode === 'read_only';
      state.isOnSite = isOnSite;
      state.emailOrdering =
        isOnSite ||
        (productSettings.available_ordering_modes.email &&
          irdSettings.ordering_mode === 'email');

      state.irdSettings = irdSettings;
      state.productSettings = productSettings;
    },
    setOrderingMode(state, action) {
      const { orderingMode } = action.payload;
      const { irdSettings, productSettings } = current(state);
      const isOnSite =
        productSettings.available_ordering_modes.on_site &&
        orderingMode === 'on_site';
      state.isReadOnly =
        productSettings.available_ordering_modes.read_only &&
        orderingMode === 'read_only';
      state.isOnSite = isOnSite;
      state.emailOrdering =
        isOnSite ||
        (productSettings.available_ordering_modes.email &&
          orderingMode === 'email');
      state.irdSettings = {
        ...irdSettings,
        ordering_mode: orderingMode,
      };
    },
    updateIrdSettings(state, action) {
      const { irdSettings } = action.payload;
      state.irdSettings = irdSettings;
    },
    setIrdSettingsLoading(state, action) {
      state.loadingIrdSettings = action.payload;
    },
    setIrdSettingsLoadingSubmit(state, action) {
      state.loadingIrdSettingsSubmit = action.payload;
    },
    setImageSelectorFile(state, action) {
      const { fileUrl } = action.payload;
      state.irdSettings.default_category_image[0].fileUrl = fileUrl;
    },
    cleanIrdSettingsSlice() {
      return initialState;
    },
  },
});

export const {
  cleanIrdSettingsSlice,
  setIrdSettings,
  setIrdSettingsLoading,
  setIrdSettingsLoadingSubmit,
  setLoadingOrders,
  setOrder,
  setOrderingMode,
  setOrders,
  setRevenueCenter,
  setRevenueCenters,
  updateIrdSettings,
  updateRevenueCenter,
} = irdSettingsSlice.actions;

export default irdSettingsSlice.reducer;
